import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import "@inovua/reactdatagrid-community/index.css";
import { ItemsRowClassName } from "../../helpers/RowClassName";
import { calculateTaxRowValue } from "../../helpers/tableUtils";
import {
    getNomenclatureCustomerPrice,
    getNomenclaturePurchasePrice,
} from "../../helpers/NomenclaturePrice";

const i18n = Object.assign({}, ReactDataGrid.defaultProps.i18n, {
    pageText: "Страница ",
    ofText: " из ",
    perPageText: "Элементов на странице",
    showingText: "Показано ",
    clearAll: "Очистить все",
    clear: "Очистить",
    showFilteringRow: "Показать фильтры",
    hideFilteringRow: "Скрыть фильтры",
    enable: "Включить",
    disable: "Выключить",
    sortAsc: "По возрастанию",
    sortDesc: "По убыванию",
    unsort: "Без сортировки",
    group: "Group",
    ungroup: "Ungroup",
    lockStart: "Lock start",
    lockEnd: "Lock end",
    unlock: "Unlock",
    columns: "Колонки",
    contains: "Содержит",
    startsWith: "Начинается с",
    endsWith: "Заканчивается на",
    notContains: "Не содержит",
    inlist: "В списке",
    notinlist: "Не в списке",
    neq: "Не эквивалентно",
    inrange: "В диапазоне",
    notinrange: "Вне диапазона",
    eq: "Эквивалентно",
    notEmpty: "Не пусто",
    empty: "Пусто",
    lt: "Меньше чем",
    lte: "Меньше чем или равно",
    gt: "Больше чем",
    gte: "Больше чем или равно",
    before: "До",
    beforeOrOn: "До или на",
    afterOrOn: "После или на",
    after: "После",
    start: "Начало",
    end: "Конец",
    dragHeaderToGroup: "Переместите заголовок для группировки",
    "calendar.todayButtonText": "Сегодня",
    "calendar.clearButtonText": "Очистить",
    "calendar.okButtonText": "OK",
    "calendar.cancelButtonText": "Отмена",
});

const ItemsTable = (props) => {
    const {
        items,
        setItem,
        fields,
        setGridRef,
        idProperty = "id",
        loading,
        watchTax = null,
        allProducts,
        allTaxes,
        typePriceId,
        filtering,
        filter,
        tableHeight = "50vh",
        maxTableHeight = "800px",
        fillHeight = false,
        className,
        selectedRowIds: externalSelectedRowIds,
        onSelectionChange,
        ...restProps
    } = props;

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const safeSetItem = useCallback((valueOrUpdater) => {
        if (isMountedRef.current) {
            setItem(valueOrUpdater);
        }
    }, [setItem]);

    const resolvedHeight = fillHeight || tableHeight === "100%" ? "100%" : tableHeight;
    const useFillHeight = resolvedHeight === "100%";

    const [internalSelectedRowIds, setInternalSelectedRowIds] = useState([]);
    const selectedRowIds = externalSelectedRowIds ?? internalSelectedRowIds;
    const setSelectedRowIds = onSelectionChange ?? setInternalSelectedRowIds;

    const tableContainerRef = useRef(null);

    const gridStyle = {
        minHeight: useFillHeight ? 0 : 320,
        borderRadius: 4,
        height: resolvedHeight,
        ...(useFillHeight ? { maxHeight: "100%" } : { maxHeight: maxTableHeight }),
    };

    const containerStyle = useFillHeight ? { height: "100%", minHeight: 0, flex: 1 } : undefined;

    const originalValuesRef = useRef({});

    // Удаление по Delete
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Delete" && selectedRowIds.length > 0 && isMountedRef.current) {
                e.preventDefault();
                safeSetItem((prevItems) => {
                    const sortedSelectedIds = [...selectedRowIds].sort((a, b) => a - b);
                    const firstDeletedIndex = prevItems.findIndex(item => item[idProperty] === sortedSelectedIds[0]);
                    const filtered = prevItems.filter(item => !selectedRowIds.includes(item[idProperty]));
                    const renumbered = filtered.map((item, index) => ({ ...item, [idProperty]: index + 1 }));

                    let newSelectedId = null;
                    if (renumbered.length > 0) {
                        const targetIndex = Math.max(0, firstDeletedIndex - 1);
                        newSelectedId = renumbered[Math.min(targetIndex, renumbered.length - 1)][idProperty];
                    }

                    setTimeout(() => {
                        if (isMountedRef.current) {
                            setSelectedRowIds(newSelectedId ? [newSelectedId] : []);
                        }
                    }, 0);

                    return renumbered;
                });
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedRowIds, safeSetItem, idProperty, setSelectedRowIds]);

    // Сброс выделения при клике вне таблицы
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isMountedRef.current && tableContainerRef.current && !tableContainerRef.current.contains(e.target)) {
                setSelectedRowIds([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [setSelectedRowIds]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (
                isMountedRef.current &&
                (e.ctrlKey || e.metaKey) &&
                e.key === 'a' &&
                tableContainerRef.current?.contains(document.activeElement)
            ) {
                e.preventDefault();
                const allIds = items.map(item => item[idProperty]);
                setSelectedRowIds(allIds);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [items, idProperty, setSelectedRowIds]);

    const enhancedFields = fields.map(col => {
        if (col.name === idProperty) {
            return {
                ...col,
                cellDOMProps: ({ data }) => ({
                    draggable: true,
                    onClick: (e) => {
                        if (!isMountedRef.current) return;
                        const isCtrlOrCmd = e.ctrlKey || e.metaKey;
                        if (isCtrlOrCmd) {
                            setSelectedRowIds(prev =>
                                prev.includes(data[idProperty])
                                    ? prev.filter(id => id !== data[idProperty])
                                    : [...prev, data[idProperty]]
                            );
                        } else {
                            setSelectedRowIds([data[idProperty]]);
                        }
                        e.stopPropagation();
                    },

                    onDragStart: (e) => {
                        e.dataTransfer.setData("text/plain", String(data[idProperty]));
                        e.dataTransfer.effectAllowed = "move";
                        const row = e.target.closest('.InovuaReactDataGrid__row');
                        if (row) row.style.opacity = "0.7";
                    },

                    onDragEnd: (e) => {
                        const row = e.target.closest('.InovuaReactDataGrid__row');
                        if (row) row.style.opacity = "";
                    },

                    onDragOver: (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        const row = e.target.closest('.InovuaReactDataGrid__row');
                        if (row && !row.classList.contains('drag-over')) {
                            row.classList.add('drag-over');
                        }
                    },

                    onDragLeave: (e) => {
                        const row = e.target.closest('.InovuaReactDataGrid__row');
                        if (row) row.classList.remove('drag-over');
                    },

                    onDrop: (e) => {
                        e.preventDefault();
                        if (!isMountedRef.current) return;
                        const row = e.target.closest('.InovuaReactDataGrid__row');
                        if (row) row.classList.remove('drag-over');

                        const draggedId = Number(e.dataTransfer.getData("text/plain"));
                        const targetId = data[idProperty];

                        if (draggedId === targetId) return;

                        safeSetItem((prev) => {
                            const fromIndex = prev.findIndex(item => item[idProperty] === draggedId);
                            const toIndex = prev.findIndex(item => item[idProperty] === targetId);

                            if (fromIndex === -1 || toIndex === -1) return prev;

                            const newItems = [...prev];
                            const [moved] = newItems.splice(fromIndex, 1);
                            newItems.splice(toIndex, 0, moved);

                            if (idProperty === 'id') {
                                return newItems.map((item, idx) => ({ ...item, id: idx + 1 }));
                            }
                            return newItems;
                        });
                    },

                    style: {
                        cursor: "grab",
                        backgroundColor: selectedRowIds.includes(data[idProperty]) ? "#e3f2fd" : "transparent",
                        fontWeight: selectedRowIds.includes(data[idProperty]) ? "bold" : "normal",
                        boxShadow: selectedRowIds.includes(data[idProperty])
                            ? "inset 0 0 0 2px #1976d2"
                            : "none",
                    },
                }),
            };
        }
        return col;
    });

    const handleGridClick = useCallback((e) => {
        if (!isMountedRef.current) return;
        const rowElement = e.target.closest('.InovuaReactDataGrid__row');
        if (!rowElement) {
            setSelectedRowIds([]);
        }
    }, [setSelectedRowIds]);

    const onEditStart = useCallback(
        ({ value, columnId, rowIndex, cellProps }) => {
            if (!isMountedRef.current) return;
            const currentValue = items[rowIndex][columnId];
            const data = [...items];

            originalValuesRef.current = {
                ...originalValuesRef.current,
                [`${rowIndex}_${columnId}`]: currentValue
            };

            if (currentValue === 0 || currentValue === '0' ||
                currentValue === 1 || currentValue === '1'
            ) {
                data[rowIndex][columnId] = '';
            }

            safeSetItem(data.map((elem) => (elem.id === cellProps.data.id ? cellProps.data : elem)));
        }, [items, safeSetItem]);

    const onEditCancel = useCallback(({ columnId, rowIndex, cellProps }) => {
        if (!isMountedRef.current) return;
        const key = `${rowIndex}_${columnId}`;
        const originalValue = originalValuesRef.current[key];

        if (cellProps.value === '' && originalValue === 0) {
            const data = [...items];
            data[rowIndex][columnId] = originalValue;
            safeSetItem(data);
        }

        delete originalValuesRef.current[key];
    }, [items, safeSetItem]);

    const onEditComplete = useCallback(
        ({ value, columnId, rowIndex, cellProps }) => {
            if (!isMountedRef.current) return;

            const data = [...items];
            const currentRow = data[rowIndex];
            const currentCell = cellProps.name;
            let validValue = value;
            currentRow.changeSumm = false;

            if (value != null) {
                if (typeof value === 'number') {
                    validValue = Math.abs(value);
                } else if (value === '') {
                    validValue = null;
                }
            }

            const getProductById = (id) => allProducts.find((val) => val.id === id);
            const getProductByNomenclatureId = (id) =>
                allProducts.find((val) => val.nomenclatureId === id);
            const getNomenclatureById = (id) =>
                allProducts.find((val) => Number(val.id) === Number(id) || Number(val.nomenclatureId) === Number(id));

            const updateFractionalCount = () => {
                const val = currentRow.count;
                currentRow.count = currentRow.hasFractional
                    ? Math.round(val * 1000) / 1000
                    : Math.floor(val);
            };

            const updateProductProperties = (product) => {
                if (!product) return;
                currentRow.isManufactured = product.isManufactured;
                currentRow.hasSerial = product.hasSerial;
                if (product.isProduct) {
                    currentRow.productId = value;
                }
            };

            const nomenclatureFields = [
                "nomenclatureOrder",
                "nomenclatureShipment",
                "nomenclatureInvoice",
                "nomenclatureComing",
                "nomenclatureRecording",
                "writeDownItemId",
                "moveItemId",
                "nomenclatureSpecif",
                "nomenclatureId",
                "nomenclatureInventory",
                "itemId"
            ];

            let product;
            if (nomenclatureFields.includes(currentCell)) {
                if (currentCell === 'itemId' || currentCell === 'nomenclatureShipment') {
                    product = getProductByNomenclatureId(value);
                } else {
                    product = getProductById(value);
                }

                if (value !== null && product) {
                    // Проверяем, изменилась ли номенклатура
                    const isSameProduct = currentRow.nomId === product.nomenclatureId;

                    if (!isSameProduct) {
                        currentRow.hasFractional = product.hasFractional;
                        currentRow.nomId = product.nomenclatureId;
                        currentRow.unit = product?.unit;
                        updateFractionalCount();
                    } else {
                        // Если та же номенклатура, но unit потерялся - восстанавливаем
                        if (!currentRow.unit && product?.unit) {
                            currentRow.unit = product.unit;
                        }

                        // hasFractional может зависеть от характеристики
                        if (currentRow.hasFractional !== product.hasFractional) {
                            currentRow.hasFractional = product.hasFractional;
                            updateFractionalCount();
                        }
                    }

                    // сброс характеристики, только если номенклатура ДРУГАЯ
                    if (!isSameProduct && currentRow[currentCell] !== value) {
                        currentRow.characteristicId = null;
                    }

                    if (currentCell === "nomenclatureInvoice" && !isSameProduct) {
                        const purchasePrice = getNomenclaturePurchasePrice(
                            product,
                            currentRow.characteristicId
                        );
                        currentRow.price = purchasePrice;
                        currentRow.clientPrice = purchasePrice;
                        if (product.taxId) {
                            currentRow.taxObjId = product.taxId;
                            const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                            currentRow.taxValue = taxx;
                        }
                    }

                    if (currentCell === "nomenclatureRecording" && !isSameProduct) {
                        currentRow.price = getNomenclaturePurchasePrice(
                            product,
                            currentRow.characteristicId
                        );
                    }

                    if (currentCell === "nomenclatureOrder" && product.taxId && !isSameProduct) {
                        currentRow.taxObjId = product.taxId;
                        const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                        currentRow.taxValue = taxx;
                    }

                    if (currentCell === "nomenclatureComing" && !isSameProduct) {
                        currentRow.price = getProductByNomenclatureId(value)?.price || 0;
                    }

                    if (currentCell === "writeDownItemId") {
                        updateProductProperties(product);
                        currentRow.nomenclatureWriteDown = product.nomenclatureId;
                    }

                    if (currentCell === "moveItemId") {
                        updateProductProperties(product);
                        currentRow.nomenclatureMove = product.nomenclatureId;
                    }

                    if (currentCell === "nomenclatureShipment") {
                        updateProductProperties(product);
                        if (!isSameProduct) {
                            currentRow.price = product.salePrice ?? product.price;
                        }
                    }

                    if (currentCell === "nomenclatureOrder") {
                        currentRow.clientPrice = getNomenclatureCustomerPrice(product, currentRow.characteristicId, typePriceId);
                    }
                } else if (value === null) {
                    if (currentCell === "nomenclatureRecording" || currentCell === "nomenclatureInvoice") {
                        currentRow.price = 0;
                    }
                    if (currentCell === "nomenclatureComing") {
                        currentRow.price = 0;
                    }
                    if (currentCell === "nomenclatureOrder") {
                        currentRow.clientPrice = 0;
                    }
                }
            }

            if (currentCell === "taxValue") {
                currentRow.changeTax = true;
            } else {
                currentRow.changeTax = false;
            }

            if (currentCell === "taxObjId") {
                currentRow.taxObjId = Number(value);
                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }
            }

            if (currentCell === "characteristicId" && currentRow.nomenclatureOrder) {
                const nomenclature = getNomenclatureById(currentRow.nomenclatureOrder);
                currentRow.clientPrice = getNomenclatureCustomerPrice(nomenclature, validValue, typePriceId);

                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }
            }

            if (currentCell === "characteristicId" && currentRow.nomenclatureInvoice) {
                const nomenclature = getNomenclatureById(currentRow.nomenclatureInvoice);
                const purchasePrice = getNomenclaturePurchasePrice(
                    nomenclature,
                    validValue
                );
                currentRow.clientPrice = purchasePrice;
                currentRow.price = purchasePrice;

                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }
            }

            if (currentCell === "characteristicId" && currentRow.nomenclatureRecording) {
                const nomenclature = getNomenclatureById(currentRow.nomenclatureRecording);
                currentRow.price = getNomenclaturePurchasePrice(
                    nomenclature,
                    validValue
                );
            }

            if (currentCell === "serial" || currentCell === "serialNumber") {
                validValue = value;
            }

            if (currentCell === "count") {
                validValue = currentRow.hasFractional
                    ? Math.round(validValue * 1000) / 1000
                    : Math.floor(validValue);

                currentRow.count = validValue;

                const count = Number(validValue) || 0;
                const price = Number(currentRow.clientPrice) || 0;
                const discount = Number(currentRow.discount) || 0;

                const rawSumm = count * price * (1 - discount / 100);
                currentRow.summ = Number(rawSumm.toFixed(2));

                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }

                currentRow.changeSumm = false;
            }

            if (currentCell === "summ") {
                const count = currentRow.count || 0;
                const discount = (currentRow.discount || 0) / 100 || 0;

                if (count <= 0) {
                    currentRow.price = 0;
                    currentRow.summ = 0;
                } else {
                    const denominator = count * (1 - discount);
                    currentRow.clientPrice = denominator !== 0
                        ? parseFloat((validValue / denominator))
                        : 0;
                }

                currentRow.summ = Number(Number(validValue).toFixed(2));
                currentRow.changeSumm = true;

                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }
            }

            if (currentCell === "price" || currentCell === "clientPrice") {
                currentRow.price = Number(validValue);

                const count = Number(currentRow.count) || 0;
                const discount = (Number(currentRow.discount) || 0) / 100;

                const calculatedSumm = count * currentRow.price * (1 - discount);
                currentRow.summ = Number(calculatedSumm.toFixed(2));

                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }
            }

            if (currentCell === "discount") {
                const sanitizedValue = String(value).replace(",", ".");
                let numValue = parseFloat(sanitizedValue) || 0;

                if (numValue < 0) numValue = 0;
                if (numValue > 99) numValue = 99;

                validValue = numValue;
                currentRow.discount = validValue;

                const count = Number(currentRow.count) || 0;
                const price = Number(currentRow.price) || 0;

                const calculatedSumm = count * price * (1 - validValue / 100);
                currentRow.summ = Number(calculatedSumm.toFixed(2));

                if (currentRow.taxObjId) {
                    const taxx = calculateTaxRowValue(currentRow, allTaxes, watchTax);
                    currentRow.taxValue = taxx;
                }
            }

            if (currentCell === "storageWriteDown" && currentRow.storageWriteDown !== value) {
                currentRow.nomenclatureWriteDown = null;
                currentRow.writeDownItemId = null;
                currentRow.characteristicId = null;
            }

            if (currentCell === "nomenclatureShipment" && currentRow.nomenclatureShipment !== value) {
                currentRow.storageShipment = null;
            }

            if (currentCell === "storageOutId" && currentRow.storageOutId !== null) {
                currentRow.storageInId = null;
            }

            data[rowIndex][columnId] = validValue;

            safeSetItem(data.map((elem) => (elem.id === cellProps.data.id ? cellProps.data : elem)));
        },
        [allProducts, items, typePriceId, allTaxes, watchTax, safeSetItem]
    );

    return (
        <div
            ref={tableContainerRef}
            className={className || ""}
            style={containerStyle}
            onClick={handleGridClick}
        >
            <ReactDataGrid
                key={watchTax?.id}
                {...restProps}
                handle={setGridRef}
                idProperty={idProperty}
                style={gridStyle}
                i18n={i18n}
                rowClassName={ItemsRowClassName}
                columns={enhancedFields}
                dataSource={items}
                loading={false}
                loadingText="Пожалуйста, подождите, получаем список товаров"
                emptyText={<h3 style={{ fontWeight: "bold", textAlign: "center" }}>В документе пока нет строк</h3>}
                onEditComplete={onEditComplete}
                onEditStart={onEditStart}
                onEditCancel={onEditCancel}
                editable={true}
                enableFiltering={filtering ?? false}
                defaultFilterValue={filter}
                showColumnMenuLockOptions={false}
                showColumnMenuGroupOptions={false}
                showColumnMenuFilterOptions={false}
                showColumnMenuSortOptions={false}
                showFilteringRow={false}
                showColumnMenuToolOnHover={true}
                enableKeyboardNavigation={false}
                sortable={false}
            />
        </div>
    );
};

ItemsTable.propTypes = {
    items: PropTypes.array.isRequired,
    fields: PropTypes.array.isRequired,
    setItem: PropTypes.func.isRequired,
    tableHeight: PropTypes.string,
    maxTableHeight: PropTypes.string,
    fillHeight: PropTypes.bool,
};

export default React.memo(ItemsTable);